/**
 * Backup and restore of one company as JSON snapshots in the remote store
 * (spec 12). Provider-independent: everything goes through the RemoteStore
 * port, so the same policy runs on Drive or on any later backend.
 *
 *  - `exportCompany` builds snapshot v3: every table for the company except
 *    blobs (attachments are indexed without their bytes), plus the device
 *    registry and a note of which device wrote this copy.
 *  - `pullSnapshot` notices that another device wrote `bokforing.json` since
 *    the local copy last agreed with it, downloads it and merges it into the
 *    local database (`./merge.ts`). The only thing it refuses is a numbering
 *    collision, which no merge can settle.
 *  - `publishSnapshot` uploads a snapshot on the condition that the remote is
 *    still the version the local copy includes — the store checks it when it
 *    can (`capabilities.conditionalPut`), and `stat` is compared first either
 *    way. A moved remote is `LedgerMovedError`, never an overwrite.
 *  - `writeBackup` is the writer used by the scheduler and the manual button:
 *    pull, export, publish, retried when the remote moves under it; monthly
 *    frozen copies in `arkiv/` (never overwritten), a final copy at year end,
 *    `backupLog` rows and `driveState` updates.
 *  - `readSnapshot` accepts v1 (the legacy flat file), v2 and v3.
 *  - `restoreSnapshot` refuses to touch an existing local company; a restore
 *    always creates a new one. Bringing an existing company up to date is a
 *    pull, not a restore.
 */
import Dexie from 'dexie';
import { z } from 'zod';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { emptyDriveState } from '@/domain/companies/defaults';
import { defaultCompanySettings } from '@/domain/company-settings/defaults';
import { defaultPaymentSources } from '@/domain/payment-sources/rules';
import { withSettingsDefaults } from '@/domain/settings/defaults';
import { currentDevice, loadDeviceIdentity, registerDevice } from '@/domain/devices/service';
import {
  ENTITY_TYPES,
  type Account,
  type AccountEvent,
  type Attachment,
  type BackupKind,
  type BackupLogEntry,
  type BankRow,
  type BankStatement,
  type Company,
  type CompanySettings,
  type DeviceRegistration,
  type DriveFolders,
  type FiscalYear,
  type InboxItem,
  type IsoDateTime,
  type MerchantRule,
  type PaymentSource,
  type PeriodLock,
  type Uuid,
  type Verifikation,
} from '@/domain/types';
import { LedgerConflictError, LedgerMovedError, collisionLabels } from '@/domain/sync/shared-ledger';
import { getJson, getRemoteStore, isRemoteError, putJson, putText, remoteStore, unusedName, type RemoteEntry, type RemoteId } from '@/cloud/store';
import { applyMergePlan, isEmptyMerge, planMerge, type MergeSummary } from './merge';
import {
  ensureCompanyTree,
  getDriveState,
  migrateLegacySnapshot,
  ROOT_FOLDER_NAME,
  SNAPSHOT_FILE_NAME,
  SUBFOLDER_NAMES,
  updateDriveState,
} from './tree';

/** @deprecated the tree lives in folders.ts; kept for older imports. */
export const BACKUP_FOLDER = ROOT_FOLDER_NAME;
export const SNAPSHOT_VERSION = 3 as const;
/** A remote copy this device could neither adopt nor merge. */
export function conflictMessage(provider = 'molnet'): string {
  return `En nyare säkerhetskopia finns i ${provider} (från en annan enhet) och kunde inte slås ihop med den här enhetens bokföring.`;
}
/** The folder holds another company's `bokforing.json`: same name, different bookkeeping. */
export function foreignSnapshotMessage(provider = 'molnet'): string {
  return `Mappen i ${provider} innehåller bokföringen för ett annat företag med samma namn. Den skrivs inte över – byt namn på företaget här, eller återställ det företaget från ${provider} i stället.`;
}
/** @deprecated use `conflictMessage(provider)`; kept for older imports. */
export const CONFLICT_MESSAGE = conflictMessage();
export const RESTORE_EXISTS_MESSAGE =
  'Företaget finns redan lokalt. En återställning skriver aldrig över befintlig bokföring – ändringar från andra enheter hämtas i stället automatiskt, och kan hämtas för hand under Mer › Lagring.';
export const NOT_A_SNAPSHOT_MESSAGE = 'Filen är ingen säkerhetskopia från den här appen.';
/** How many times `writeBackup` follows a moved remote (pull, merge, publish again). */
export const MAX_PUBLISH_ATTEMPTS = 3;

export interface CompanySnapshotV1 {
  version: 1;
  exportedAt: IsoDateTime;
  company: Company;
  fiscalYears: FiscalYear[];
  accounts: Account[];
  accountEvents: AccountEvent[];
  verifikationer: Verifikation[];
}

/** A snapshot carries no bytes, so it carries no claim about local ones either. */
export type AttachmentIndexEntry = Omit<Attachment, 'blob' | 'thumbnail' | 'hasLocalBlob'>;

export interface CompanySnapshotV2 {
  version: 2;
  exportedAt: IsoDateTime;
  company: Company;
  companySettings: CompanySettings;
  fiscalYears: FiscalYear[];
  accounts: Account[];
  accountEvents: AccountEvent[];
  verifikationer: Verifikation[];
  paymentSources: PaymentSource[];
  merchantRules: MerchantRule[];
  periodLocks: PeriodLock[];
  bankStatements: BankStatement[];
  bankRows: BankRow[];
  inboxItems: InboxItem[];
  attachmentsIndex: AttachmentIndexEntry[];
}

/**
 * Which installation wrote this copy. Provenance for a human reading the
 * archive or a restore screen — never an input to the conflict check, which
 * compares the remote entry's opaque `version` and nothing else.
 */
export interface SnapshotOrigin {
  deviceId: Uuid;
  /** The name as it was at export time; the registry holds the current one. */
  deviceName: string;
}

export interface CompanySnapshotV3 extends Omit<CompanySnapshotV2, 'version'> {
  version: 3;
  /** Names for the `deviceId` on every verifikation, so a restore keeps them. */
  devices: DeviceRegistration[];
  /** Absent in copies written before v3, and when no identity was established. */
  writtenBy?: SnapshotOrigin;
}

/** @deprecated alias for callers written against v1. */
export type CompanySnapshot = CompanySnapshotV3;

export class BackupConflictError extends Error {
  constructor(message = CONFLICT_MESSAGE) {
    super(message);
    this.name = 'BackupConflictError';
  }
}

export class RestoreRefusedError extends Error {
  constructor(message = RESTORE_EXISTS_MESSAGE) {
    super(message);
    this.name = 'RestoreRefusedError';
  }
}

export class SnapshotFormatError extends Error {
  constructor(
    message = NOT_A_SNAPSHOT_MESSAGE,
    public readonly technical?: string,
  ) {
    super(message);
    this.name = 'SnapshotFormatError';
  }
}

// ---- export ------------------------------------------------------------------

export async function exportCompany(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<CompanySnapshotV3> {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error('Company not found');
  const byCompany = <T>(table: { where(index: string): { equals(v: string): { toArray(): Promise<T[]> } } }) =>
    table.where('companyId').equals(companyId).toArray();
  const [fiscalYears, accounts, accountEvents, verifikationer, paymentSources, merchantRules, periodLocks, bankStatements, bankRows, inboxItems, attachments] =
    await Promise.all([
      byCompany<FiscalYear>(db.fiscalYears),
      byCompany<Account>(db.accounts),
      byCompany<AccountEvent>(db.accountEvents),
      byCompany<Verifikation>(db.verifikationer),
      byCompany<PaymentSource>(db.paymentSources),
      byCompany<MerchantRule>(db.merchantRules),
      byCompany<PeriodLock>(db.periodLocks),
      byCompany<BankStatement>(db.bankStatements),
      byCompany<BankRow>(db.bankRows),
      byCompany<InboxItem>(db.inboxItems),
      byCompany<Attachment>(db.attachments),
    ]);
  const companySettings = (await db.companySettings.get(companyId)) ?? defaultCompanySettings(company);
  const devices = await db.devices.where('companyId').equals(companyId).toArray();
  /*
   * The cached identity first: it costs no read, so an export that happens to
   * run inside someone else's transaction never needs `settings` in scope.
   * Inside a transaction the cache is all there is: the shared ledger exports
   * the company from within a booking's own transaction, whose scope does not
   * include `settings`, and a snapshot without a `writtenBy` beats a failed
   * booking.
   */
  const device = currentDevice() ?? (Dexie.currentTransaction ? null : await loadDeviceIdentity(db));
  return {
    version: SNAPSHOT_VERSION,
    exportedAt: new Date().toISOString(),
    devices,
    ...(device ? { writtenBy: { deviceId: device.id, deviceName: device.name } } : {}),
    company,
    companySettings,
    fiscalYears,
    accounts,
    accountEvents,
    verifikationer,
    paymentSources,
    merchantRules,
    periodLocks,
    bankStatements,
    bankRows,
    inboxItems,
    attachmentsIndex: attachments.map(stripBlobs),
  };
}

export function stripBlobs(a: Attachment): AttachmentIndexEntry {
  const { blob: _blob, thumbnail: _thumbnail, hasLocalBlob: _hasLocalBlob, ...rest } = a;
  return rest;
}

// ---- read (v1 + v2) ----------------------------------------------------------

const companySchema = z.looseObject({
  id: z.string().min(1),
  name: z.string(),
  orgNumber: z.string(),
  entityType: z.enum(ENTITY_TYPES),
  createdAt: z.string(),
});

const v1Schema = z.looseObject({
  version: z.literal(1),
  exportedAt: z.string(),
  company: companySchema,
  fiscalYears: z.array(z.looseObject({ id: z.string() })),
  accounts: z.array(z.looseObject({ id: z.string() })),
  accountEvents: z.array(z.looseObject({ id: z.string() })),
  verifikationer: z.array(z.looseObject({ id: z.string() })),
});

const v2Schema = v1Schema.extend({
  version: z.literal(2),
  companySettings: z.looseObject({ id: z.string() }),
  paymentSources: z.array(z.looseObject({ id: z.string() })),
  merchantRules: z.array(z.looseObject({ id: z.string() })).default([]),
  periodLocks: z.array(z.looseObject({ id: z.string() })).default([]),
  bankStatements: z.array(z.looseObject({ id: z.string() })).default([]),
  bankRows: z.array(z.looseObject({ id: z.string() })).default([]),
  inboxItems: z.array(z.looseObject({ id: z.string() })).default([]),
  attachmentsIndex: z.array(z.looseObject({ id: z.string() })).default([]),
});

const v3Schema = v2Schema.extend({
  version: z.literal(3),
  devices: z.array(z.looseObject({ id: z.string() })).default([]),
  writtenBy: z.looseObject({ deviceId: z.string(), deviceName: z.string() }).optional(),
});

/**
 * v2 knew no devices. An empty registry is the honest answer: the copy was
 * written before the app had an identity, so no verifikation in it carries a
 * `deviceId` either.
 */
export function upgradeSnapshotV2(v2: CompanySnapshotV2): CompanySnapshotV3 {
  return { ...v2, version: 3, devices: [] };
}

/** Fill in what v1 did not have; pure. */
export function upgradeSnapshot(v1: CompanySnapshotV1): CompanySnapshotV3 {
  const company = v1.company;
  const sources = defaultPaymentSources(company, v1.exportedAt);
  const bank = sources.find((s) => s.kind === 'bank' && s.active) ?? null;
  return {
    version: SNAPSHOT_VERSION,
    exportedAt: v1.exportedAt,
    company,
    companySettings: defaultCompanySettings(company, v1.exportedAt, {
      defaultPaymentSourceId: bank?.id ?? null,
      onboarding: { completedAt: v1.exportedAt, driveSkippedAt: null, aiSkippedAt: null },
    }),
    fiscalYears: v1.fiscalYears,
    accounts: v1.accounts,
    accountEvents: v1.accountEvents,
    verifikationer: v1.verifikationer,
    paymentSources: sources,
    merchantRules: [],
    periodLocks: [],
    bankStatements: [],
    bankRows: [],
    inboxItems: [],
    attachmentsIndex: [],
    devices: [],
  };
}

/** Validate a downloaded JSON document and normalise it to v3. Throws SnapshotFormatError. */
export function readSnapshot(raw: unknown): CompanySnapshotV3 {
  if (!raw || typeof raw !== 'object') throw new SnapshotFormatError(NOT_A_SNAPSHOT_MESSAGE, 'not an object');
  const version = (raw as { version?: unknown }).version;
  if (version === 1) {
    const parsed = v1Schema.safeParse(raw);
    if (!parsed.success) throw new SnapshotFormatError(NOT_A_SNAPSHOT_MESSAGE, parsed.error.message);
    return upgradeSnapshot(parsed.data as unknown as CompanySnapshotV1);
  }
  if (version === 2) {
    const parsed = v2Schema.safeParse(raw);
    if (!parsed.success) throw new SnapshotFormatError(NOT_A_SNAPSHOT_MESSAGE, parsed.error.message);
    return upgradeSnapshotV2(parsed.data as unknown as CompanySnapshotV2);
  }
  if (version === 3) {
    const parsed = v3Schema.safeParse(raw);
    if (!parsed.success) throw new SnapshotFormatError(NOT_A_SNAPSHOT_MESSAGE, parsed.error.message);
    return parsed.data as unknown as CompanySnapshotV3;
  }
  throw new SnapshotFormatError(`Okänd version på säkerhetskopian: ${String(version)}`, `version ${String(version)}`);
}

// ---- restore -----------------------------------------------------------------

let restoring = 0;
/** True while a restore transaction runs; the scheduler ignores its hooks then. */
export function isRestoring(): boolean {
  return restoring > 0;
}

export interface RestoreOrigin {
  /** Portable backups provide the bytes and must not inherit cloud references. */
  localAttachments?: ReadonlyMap<string, Blob>;
  /** The `bokforing.json` this snapshot came from; lets the next backup overwrite it. */
  snapshotFileId?: RemoteId;
  /**
   * The entry's opaque `version` at download time, stored as
   * `driveState.lastBackupModifiedTime` (the field keeps its name until the
   * Dexie table is renamed). Compared for equality only.
   */
  modifiedTime?: string;
}

/**
 * Insert a snapshot as a new local company. Refuses when the company id is
 * already present locally (verifikationer are append-only and must never be
 * rewritten). Everything lands in one transaction.
 */
export async function restoreSnapshot(snapshot: CompanySnapshotV3, db: BookkeepingDB = defaultDb, origin: RestoreOrigin = {}): Promise<Company> {
  restoring++;
  try {
    await db.transaction(
      'rw',
      [
        db.companies,
        db.fiscalYears,
        db.accounts,
        db.accountEvents,
        db.verifikationer,
        db.settings,
        db.companySettings,
        db.paymentSources,
        db.inboxItems,
        db.attachments,
        db.bankStatements,
        db.bankRows,
        db.merchantRules,
        db.periodLocks,
        db.driveState,
        db.devices,
      ],
      async () => {
        await assertCompanyAbsent(snapshot.company, db);
        await db.companies.add(snapshot.company);
        await db.fiscalYears.bulkAdd(snapshot.fiscalYears);
        await db.accounts.bulkAdd(snapshot.accounts);
        await db.accountEvents.bulkAdd(snapshot.accountEvents);
        await db.verifikationer.bulkAdd(snapshot.verifikationer);
        await db.companySettings.add({ ...snapshot.companySettings, id: snapshot.company.id, companyId: snapshot.company.id });
        await db.paymentSources.bulkAdd(snapshot.paymentSources);
        await db.merchantRules.bulkAdd(snapshot.merchantRules);
        await db.periodLocks.bulkAdd(snapshot.periodLocks);
        await db.bankStatements.bulkAdd(snapshot.bankStatements);
        await db.bankRows.bulkAdd(snapshot.bankRows);
        await db.inboxItems.bulkAdd(snapshot.inboxItems);
        /*
         * The devices that wrote this bookkeeping, names included — this
         * machine is not one of them until it writes something itself, and it
         * registers under its own id when it does.
         */
        await db.devices.bulkAdd(snapshot.devices);
        // Bytes live in the remote store; local copies are re-fetched on
        // demand. No bytes in a snapshot: `hasLocalBlob: 0` is the work list
        // the receipt hydration walks to fetch them back.
        await db.attachments.bulkAdd(snapshot.attachmentsIndex.map((a): Attachment => {
          const blob = origin.localAttachments?.get(a.id) ?? null;
          if (origin.localAttachments && !blob) throw new Error(`Säkerhetskopian saknar bilagan ${a.name}.`);
          return { ...a, blob, hasLocalBlob: blob ? 1 : 0, thumbnail: blob?.type.startsWith('image/') ? blob : new Blob([]) };
        }));
        const state = emptyDriveState(snapshot.company.id);
        if (origin.snapshotFileId) state.snapshotFileId = origin.snapshotFileId;
        if (origin.modifiedTime) state.lastBackupModifiedTime = origin.modifiedTime;
        if (origin.snapshotFileId) state.lastBackupAt = new Date().toISOString();
        await db.driveState.put(state);
        const settings = withSettingsDefaults(await db.settings.get('settings'));
        if (!settings.activeCompanyId) await db.settings.put({ ...settings, activeCompanyId: snapshot.company.id });
      },
    );
  } finally {
    restoring--;
  }
  return snapshot.company;
}

/** Same identity under a different UUID is still the same company. Never overwrite or duplicate it. */
export async function assertCompanyAbsent(company: Company, db: BookkeepingDB): Promise<void> {
  const org = company.orgNumber.replace(/\D/g, '').slice(-10);
  const existing = await db.companies.toArray();
  if (existing.some((c) => c.id === company.id || (org && c.orgNumber.replace(/\D/g, '').slice(-10) === org))) {
    throw new RestoreRefusedError();
  }
}

// ---- write -------------------------------------------------------------------

export interface BackupDeps {
  ensureTree: (company: Company, db: BookkeepingDB) => Promise<DriveFolders>;
  migrateLegacy: (company: Company, folders: DriveFolders) => Promise<RemoteEntry | undefined>;
  /** Metadata, or undefined when the entry is gone. Never throws `not_found`. */
  stat: (id: RemoteId) => Promise<RemoteEntry | undefined>;
  findByName: (name: string, parentId: RemoteId) => Promise<RemoteEntry | undefined>;
  /**
   * Upload JSON. `ifVersion` is the optimistic lock: only replace `fileId`
   * while its version is still this one, else reject with a `conflict`.
   * Honoured by the store when `conditionalPut()` is true; ignored otherwise.
   */
  uploadJson: (i: { name: string; parentId: RemoteId; data: unknown; fileId?: RemoteId; ifVersion?: string }) => Promise<RemoteEntry>;
  /**
   * Download and parse a JSON file: what `pullSnapshot` merges another
   * device's copy with. Without it a moved remote is a conflict, as it was
   * before devices could share a bookkeeping.
   */
  readJson?: <T>(id: RemoteId) => Promise<T>;
  /** Upload a text file (the monthly arkivutdrag). */
  uploadText?: (i: { name: string; parentId: RemoteId; text: string; mediaType: string }) => Promise<RemoteEntry>;
  /**
   * Render the archive extract for a company's open year. Injected so the
   * backup module stays free of the reports layer, and so tests can skip it.
   */
  buildMonthlyArchive?: (companyId: Uuid, db: BookkeepingDB) => Promise<{ name: string; html: string } | null>;
  /** Whether the store checks `ifVersion` itself (`capabilities.conditionalPut`). */
  conditionalPut?: () => boolean;
  /** Provider label for Swedish messages ("Koofr"). */
  provider?: () => string;
  now: () => Date;
}

/**
 * Build the monthly archive extract for the company's current open year.
 *
 * Loaded on demand: this pulls in the whole reports layer, and a monthly
 * frozen copy happens once a month, while `backup.ts` itself is imported at
 * start-up by the scheduler. Returns null when there is no open year to
 * describe.
 */
async function monthlyArchiveBuilder(companyId: Uuid, db: BookkeepingDB): Promise<{ name: string; html: string } | null> {
  const years = await db.fiscalYears.where('companyId').equals(companyId).sortBy('startDate');
  const open = years.filter((fy) => fy.status === 'open');
  const fy = open[open.length - 1] ?? years[years.length - 1];
  if (!fy) return null;
  const { yearEndArchive } = await import('@/domain/year-end/service');
  const archive = await yearEndArchive(companyId, fy.id, db);
  return { name: archive.arkivutdragName, html: archive.arkivutdragHtml };
}

export const defaultBackupDeps: BackupDeps = {
  ensureTree: (company, db) => ensureCompanyTree(company, db),
  migrateLegacy: migrateLegacySnapshot,
  stat: (id) => getRemoteStore().stat(id),
  findByName: (name, parentId) => getRemoteStore().findByName(name, parentId),
  uploadJson: (i) =>
    putJson(getRemoteStore(), {
      name: i.name,
      parentId: i.parentId,
      data: i.data,
      ...(i.fileId ? { id: i.fileId } : {}),
      ...(i.ifVersion ? { ifVersion: i.ifVersion } : {}),
    }),
  readJson: (id) => getJson(getRemoteStore(), id),
  uploadText: (i) => putText(getRemoteStore(), { name: i.name, parentId: i.parentId, text: i.text, mediaType: i.mediaType }),
  buildMonthlyArchive: monthlyArchiveBuilder,
  conditionalPut: () => remoteStore()?.capabilities.conditionalPut ?? false,
  provider: () => remoteStore()?.label ?? 'molnet',
  now: () => new Date(),
};

function providerOf(deps: BackupDeps): string {
  return deps.provider?.() ?? 'molnet';
}

/** The remote changed under us: a `LedgerMovedError` that names the provider for the message the user may end up seeing. */
function movedError(deps: BackupDeps): LedgerMovedError {
  return Object.assign(new LedgerMovedError(), { provider: providerOf(deps) });
}

// ---- pull ----------------------------------------------------------------------

export interface PullResult {
  /** True when rows were added or changed locally. */
  changed: boolean;
  /** The remote entry's opaque version the local copy now includes; undefined without a remote snapshot. */
  version?: string;
  /** What the merge did, when there was one. */
  summary?: MergeSummary;
  /** Which device wrote the copy that was merged, when the snapshot said. */
  writtenBy?: SnapshotOrigin;
}

/**
 * Bring the local company up to date with the remote `bokforing.json`.
 *
 * Nothing happens while the remote entry's version is the one the local copy
 * already includes. Otherwise the file is downloaded, checked to be this
 * company's, and merged: append-only tables by union, working state by the
 * rules in `./merge.ts`. The one thing that cannot be merged is two devices
 * having booked the same number; that is recorded on `driveState` and thrown
 * as `LedgerConflictError`, and nothing is written.
 *
 * Runs outside any transaction (the merge opens its own).
 */
export async function pullSnapshot(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: BackupDeps = defaultBackupDeps): Promise<PullResult> {
  const state = await getDriveState(companyId, db);
  const at = deps.now().toISOString();

  // A known file id is enough to ask after; the folder tree is only needed to
  // find a file this device has never written.
  let file = state.snapshotFileId ? await deps.stat(state.snapshotFileId) : undefined;
  if ((!file || file.trashed) && state.folders) file = await deps.findByName(SNAPSHOT_FILE_NAME, state.folders.company);
  if (!file || file.trashed) {
    // No remote snapshot: nothing to merge, and a stale id is not worth keeping.
    if (state.snapshotFileId) {
      await updateDriveState(
        companyId,
        (s) => {
          delete s.snapshotFileId;
          delete s.lastBackupModifiedTime;
          s.lastPulledAt = at;
        },
        db,
      );
    }
    return { changed: false };
  }

  const known = state.snapshotFileId === file.id ? state.lastBackupModifiedTime : undefined;
  if (known && file.version && file.version === known) {
    await updateDriveState(companyId, (s) => void (s.lastPulledAt = at), db);
    return { changed: false, version: file.version };
  }

  if (!deps.readJson) throw new BackupConflictError(conflictMessage(providerOf(deps)));
  const snapshot = readSnapshot(await deps.readJson<unknown>(file.id));
  if (snapshot.company.id !== companyId) throw new BackupConflictError(foreignSnapshotMessage(providerOf(deps)));

  const local = await exportCompany(companyId, db);
  const plan = planMerge(local, snapshot);
  if (plan.collisions.length) {
    const error = new LedgerConflictError(plan.collisions, providerOf(deps));
    await updateDriveState(
      companyId,
      (s) => {
        s.ledgerConflict = { at, message: error.message, numbers: collisionLabels(plan.collisions) };
        s.lastPulledAt = at;
      },
      db,
    );
    throw error;
  }
  const changed = !isEmptyMerge(plan);
  if (changed) await applyMergePlan(plan, db);
  const version = file.version;
  await updateDriveState(
    companyId,
    (s) => {
      s.snapshotFileId = file.id;
      if (version) s.lastBackupModifiedTime = version;
      else delete s.lastBackupModifiedTime;
      s.lastPulledAt = at;
      delete s.ledgerConflict;
    },
    db,
  );
  return {
    changed,
    ...(version ? { version } : {}),
    summary: plan.summary,
    ...(snapshot.writtenBy ? { writtenBy: snapshot.writtenBy } : {}),
  };
}

// ---- publish -------------------------------------------------------------------

export interface WriteBackupOptions {
  /** Overwrite even when the remote snapshot changed since we last wrote (explicit user choice only). */
  force?: boolean;
  /** Label for the `arkiv/bokforing-<label>-slutlig.json` copy at year end. */
  yearLabel?: string;
}

export interface PublishOptions extends WriteBackupOptions {
  kind: BackupKind;
  /** Timestamp of the attempt, for the log row. Defaults to `deps.now()`. */
  at?: string;
  /**
   * Also write the frozen monthly copy, the arkivutdrag and the year-end
   * copy. Off when publishing from inside a booking's transaction: those read
   * the database through transactions of their own, which would wait for the
   * booking's transaction, which is waiting for this upload.
   */
  extras?: boolean;
}

export function monthKey(d: Date): string {
  return d.toISOString().slice(0, 7);
}

/**
 * Upload `snapshot` as the company's `bokforing.json`, on the condition that
 * the remote is still the version the local copy includes.
 *
 * Throws `LedgerMovedError` when it is not — a remote written by another
 * device since, or a `bokforing.json` this device has never seen — so the
 * caller can pull, merge and try again. `force` skips the condition: the
 * explicit "skriv över" choice, and the copy a storage move just made.
 *
 * Touches only `driveState` and `backupLog`, in transactions of its own, so
 * it can run under `Dexie.waitFor` while a booking's transaction over every
 * other table stays open.
 */
export async function publishSnapshot(
  companyId: Uuid,
  snapshot: CompanySnapshotV3,
  db: BookkeepingDB = defaultDb,
  deps: BackupDeps = defaultBackupDeps,
  opts: PublishOptions,
): Promise<BackupLogEntry> {
  const at = opts.at ?? deps.now().toISOString();
  const before = await getDriveState(companyId, db);
  const pendingAtStart = before.pendingChanges;
  // The snapshot's own company row: reading `companies` here would wait on a
  // booking transaction that is waiting on this upload.
  const company = snapshot.company;
  const folders = await deps.ensureTree(company, db);
  let snapshotFileId = before.snapshotFileId;
  let known = before.lastBackupModifiedTime;

  if (!snapshotFileId) {
    const migrated = await deps.migrateLegacy(company, folders);
    if (migrated) {
      // The v1 app's own file, just moved into place: overwrite it, but only
      // as the version we moved.
      snapshotFileId = migrated.id;
      known = migrated.version;
    } else {
      const existing = await deps.findByName(SNAPSHOT_FILE_NAME, folders.company);
      if (existing) {
        // A snapshot this device never wrote: another device's, or the copy
        // a storage move just made. Adopt it only when told to; a pull is
        // how it is merged.
        if (!opts.force) throw movedError(deps);
        snapshotFileId = existing.id;
        known = existing.version;
      }
    }
  }

  if (snapshotFileId) {
    const remote = await deps.stat(snapshotFileId);
    if (!remote || remote.trashed) {
      snapshotFileId = undefined;
      known = undefined;
    } else if (!opts.force && remote.version !== known) {
      // `version` is opaque: only "different from what the local copy
      // includes" is meaningful, never which of the two is newer.
      throw movedError(deps);
    }
  }

  let file: RemoteEntry;
  try {
    file = await deps.uploadJson({
      name: SNAPSHOT_FILE_NAME,
      parentId: folders.company,
      data: snapshot,
      ...(snapshotFileId ? { fileId: snapshotFileId } : {}),
      ...(snapshotFileId && known && !opts.force && deps.conditionalPut?.() ? { ifVersion: known } : {}),
    });
  } catch (e) {
    // The store's own check: someone wrote between our stat and our put, or
    // created the file we were about to create.
    if (isRemoteError(e, 'conflict')) throw movedError(deps);
    throw e;
  }

  const month = monthKey(deps.now());
  let frozenMonth = before.lastMonthlyFrozen;
  if (opts.extras !== false && (frozenMonth !== month || opts.kind === 'monthly')) {
    const name = `${month}.json`;
    if (!(await deps.findByName(name, folders.arkiv))) {
      await deps.uploadJson({ name, parentId: folders.arkiv, data: snapshot });
    }
    /*
     * The JSON snapshot only this app can read is a restore file, not
     * räkenskapsinformation anyone could inspect in seven years. The frozen
     * month therefore also gets a self-contained arkivutdrag: verifikations-
     * lista, huvudbok, resultat, balans, moms, kontoplan, systemdokumentation
     * and behandlingshistorik in one HTML file that opens in any browser.
     * Best effort — a failure here must never fail the backup.
     */
    const htmlName = `${month}.html`;
    if (deps.uploadText && deps.buildMonthlyArchive && !(await deps.findByName(htmlName, folders.arkiv))) {
      try {
        const archive = await deps.buildMonthlyArchive(companyId, db);
        if (archive) await deps.uploadText({ name: htmlName, parentId: folders.arkiv, text: archive.html, mediaType: 'text/html;charset=utf-8' });
      } catch {
        /* the snapshot is already safe; the extract retries next month */
      }
    }
    frozenMonth = month;
  }
  if (opts.extras !== false && opts.kind === 'year_end') {
    const label = opts.yearLabel ?? snapshot.fiscalYears.filter((fy) => fy.status === 'open').sort((a, b) => a.startDate.localeCompare(b.startDate))[0]?.label ?? month.slice(0, 4);
    const base = `bokforing-${label.replace(/\//g, '-')}-slutlig`;
    const name = await unusedName((candidate) => deps.findByName(candidate, folders.arkiv), (n) => (n === 1 ? `${base}.json` : `${base}-${n}.json`));
    await deps.uploadJson({ name, parentId: folders.arkiv, data: snapshot });
  }

  const bytes = file.size;
  const entry: BackupLogEntry = {
    id: crypto.randomUUID(),
    companyId,
    at,
    kind: opts.kind,
    ok: true,
    fileId: file.id,
    ...(bytes !== undefined && Number.isFinite(bytes) ? { bytes } : {}),
  };
  await db.transaction('rw', db.driveState, db.backupLog, async () => {
    await updateDriveState(
      companyId,
      (s) => {
        s.snapshotFileId = file.id;
        s.lastBackupAt = at;
        if (file.version) s.lastBackupModifiedTime = file.version;
        else delete s.lastBackupModifiedTime;
        s.pendingChanges = Math.max(0, s.pendingChanges - pendingAtStart);
        if (s.pendingChanges === 0) delete s.dirtySince;
        delete s.lastError;
        delete s.ledgerConflict;
        s.needsSignIn = false;
        if (frozenMonth) s.lastMonthlyFrozen = frozenMonth;
      },
      db,
    );
    await db.backupLog.add(entry);
  });
  return entry;
}

/**
 * Write the company's snapshot to the remote store. Resolves with the
 * `backupLog` row it created. Throws (after logging) on failure; a store that
 * cannot act silently surfaces as a `RemoteStoreError` of code `needs_sign_in`
 * and only flips `needsSignIn` (no log row).
 *
 * A remote that another device wrote since is merged first and the publish
 * tried again, up to `MAX_PUBLISH_ATTEMPTS`. That, and never overwriting what
 * was not merged, is what lets two devices keep one bookkeeping. `force`
 * skips both: the user's explicit choice after a collision, or the copy a
 * storage move just made.
 */
export async function writeBackup(companyId: Uuid, kind: BackupKind, db: BookkeepingDB = defaultDb, deps: BackupDeps = defaultBackupDeps, opts: WriteBackupOptions = {}): Promise<BackupLogEntry> {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error(`Company ${companyId} not found`);
  const at = deps.now().toISOString();

  try {
    for (let attempt = 1; ; attempt++) {
      if (!opts.force) await pullSnapshot(companyId, db, deps);
      /*
       * Name this device in the copy it is about to write. Best effort: a
       * registry row is a label, and a backup must never fail over one.
       */
      try {
        await registerDevice(companyId, db);
      } catch {
        /* no identity yet, or settings unreadable; the snapshot just omits it */
      }
      const snapshot = await exportCompany(companyId, db);
      try {
        return await publishSnapshot(companyId, snapshot, db, deps, { ...opts, kind, at, extras: true });
      } catch (e) {
        if (!(e instanceof LedgerMovedError)) throw e;
        if (opts.force || attempt >= MAX_PUBLISH_ATTEMPTS || !deps.readJson) throw new BackupConflictError(conflictMessage(providerOf(deps)));
      }
    }
  } catch (e) {
    if (isRemoteError(e, 'needs_sign_in')) {
      await updateDriveState(companyId, (s) => void (s.needsSignIn = true), db);
      throw e;
    }
    const message = e instanceof Error ? e.message : String(e);
    await db.transaction('rw', db.driveState, db.backupLog, async () => {
      await updateDriveState(companyId, (s) => void (s.lastError = message), db);
      await db.backupLog.add({ id: crypto.randomUUID(), companyId, at, kind, ok: false, error: message });
    });
    throw e;
  }
}

/** Manual "Säkerhetskopiera nu"; kept for existing callers. */
export async function backupNow(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<RemoteEntry | undefined> {
  const entry = await writeBackup(companyId, 'manual', db);
  return entry.fileId ? getRemoteStore().stat(entry.fileId) : undefined;
}

// ---- list & restore ----------------------------------------------------------

export type BackupFileKind = 'snapshot' | 'frozen' | 'legacy';

export interface BackupInfo extends RemoteEntry {
  kind: BackupFileKind;
  /** `<Namn> (<orgnr>)` for files inside a company folder. */
  companyFolderName?: string;
}

const JSON_MEDIA_TYPE = 'application/json';

/**
 * Every snapshot in the app's folder: `bokforing.json` per company, frozen
 * `arkiv/` copies and legacy flat files from the v1 app.
 *
 * Walks the tree one folder at a time rather than asking the backend for
 * "every JSON file you can see" — that question only has an answer on Drive,
 * where drive.file already narrows the result to this app's own files.
 */
export async function listBackups(): Promise<BackupInfo[]> {
  const store = getRemoteStore();
  const root = await store.findByName(ROOT_FOLDER_NAME, undefined, 'folder');
  if (!root) return [];
  const out: BackupInfo[] = [];
  const rootChildren = await store.list(root.id);

  for (const entry of rootChildren) {
    if (!entry.isFolder && entry.mediaType === JSON_MEDIA_TYPE) out.push({ ...entry, kind: 'legacy' });
  }

  for (const companyFolder of rootChildren.filter((e) => e.isFolder)) {
    const children = await store.list(companyFolder.id);
    for (const entry of children) {
      if (!entry.isFolder && entry.name === SNAPSHOT_FILE_NAME) out.push({ ...entry, kind: 'snapshot', companyFolderName: companyFolder.name });
    }
    const arkiv = children.find((e) => e.isFolder && e.name === SUBFOLDER_NAMES.arkiv);
    if (!arkiv) continue;
    for (const entry of await store.list(arkiv.id)) {
      if (!entry.isFolder && entry.mediaType === JSON_MEDIA_TYPE) out.push({ ...entry, kind: 'frozen', companyFolderName: companyFolder.name });
    }
  }

  const rank: Record<BackupFileKind, number> = { snapshot: 0, frozen: 1, legacy: 2 };
  return out.sort((a, b) => rank[a.kind] - rank[b.kind] || (b.modifiedAt ?? '').localeCompare(a.modifiedAt ?? ''));
}

export interface RestoreDeps {
  readJson: <T>(fileId: RemoteId) => Promise<T>;
  stat: (fileId: RemoteId) => Promise<RemoteEntry | undefined>;
  /** Resolve (or create) the company's folder tree and cache it in driveState. */
  ensureTree: (company: Company, db: BookkeepingDB) => Promise<DriveFolders>;
}

export const defaultRestoreDeps: RestoreDeps = {
  readJson: (fileId) => getJson(getRemoteStore(), fileId),
  stat: (fileId) => getRemoteStore().stat(fileId),
  ensureTree: (company, db) => ensureCompanyTree(company, db),
};

/**
 * Download, validate and restore a snapshot (v1 or v2) as a new local company.
 *
 * The restored state then gets its folder tree. The user signed in and picked
 * a file out of their own EgenBalans folder to get here, so leaving the
 * company marked "Drive ej ansluten" — no backups, no receipt filing until
 * they connect again — contradicted what they just did.
 *
 * A failure there is not a failed restore: the bookkeeping is already in the
 * database, and Mer › Lagring › Google Drive can connect it later.
 */
export async function restoreFromBackup(fileId: RemoteId, db: BookkeepingDB = defaultDb, deps: RestoreDeps = defaultRestoreDeps): Promise<Company> {
  const [raw, meta] = await Promise.all([deps.readJson<unknown>(fileId), deps.stat(fileId)]);
  const snapshot = readSnapshot(raw);
  const origin: RestoreOrigin =
    meta?.name === SNAPSHOT_FILE_NAME ? { snapshotFileId: fileId, ...(meta.version ? { modifiedTime: meta.version } : {}) } : {};
  const company = await restoreSnapshot(snapshot, db, origin);
  try {
    await deps.ensureTree(company, db);
  } catch {
    // Offline, or the token expired between the download and here.
  }
  return company;
}
